define(function (require) {
  const es6MiddleUtils = require('src/es6MiddleUtils');
  const LocaleResolver = require('i18n/localeResolver');
  const registerModule = require('modules/registerModule');
  const { storeModuleTypes, environmentActionTypes, i18n, Logger, store } = require("vue-infra");
  const LEConfig = require('assets/config/le-env-conf');
  const mainVueApps = require('src/components/mainVueApps');
  const logger = Logger.getLogger("LEFramework");
  const applications = {};
  const DEV_STRING = 'dev';
  const APP_STRING = 'app';
  // CDN application Names.
  // TODO: module loader.

  /**
   * Injects a <script> element in the document head for the given url
   * returning a promise that resolves after the script successfully loads. If there is an error
   * loading, it logs and error but still resolves gracefully, to not block loading.
   */
  function scriptTagGenerator({url, isEsModule}) {
    return new Promise ((resolve) => {
      const script = document.createElement('script');
      if (isEsModule) {
        script.type = 'module';
      }
      script.src = url;
      script.async = false;

      script.onload = script.onreadystatechange = function() {
        script.onreadystatechange = script.onload = null;
        resolve();
      };

      script.onerror = function(e) {
        logger.error(`module script src ${url} fail to load, ${e}`);
        resolve();
      };

      const head = document.getElementsByTagName('head')[0];
      (head || document.body).appendChild(script);
    });
  }

  function calculateAppNameFromCdnName(cdnName){
    const staticPrefix = 'lp-le-';
    if(cdnName.includes(staticPrefix)) {
      return cdnName.replace(staticPrefix, '');
    }
    return cdnName;
  }

  function calculateApplicationAppIdFromName(appName) {
    const delimiter = '-';
    if(appName.includes(delimiter)){
      return appName.split(delimiter).map(name => name[0]).join('');
    } else {
      return appName.substring(0,3);
    }
  }

  function calculateConf(cdnName, version, useOrigLpCdnDomain) {
    const applicationType = LEConfig.dev === true ? DEV_STRING : APP_STRING;
    let conf = LEConfig.appsConfig[applicationType];
    if(LEConfig.dev === true) {
      conf.domain = LEConfig.leDevDomain;
      if(useOrigLpCdnDomain){
        conf = LEConfig.appsConfig.app;
        conf.domain = window.leResourceDomain || LEConfig.defaultLeResourceDomain;
      }
    } else {
      conf.domain = window.leResourceDomain;
    }
    conf.cdnName = cdnName;
    conf.name = calculateAppNameFromCdnName(cdnName);
    conf.appId = getAppIdFromCdnName(cdnName);
    conf.publicPath = conf.basePathTemplate.replace('{lpCdnDomain}', conf.domain)
      .replace('{appName}', conf.name)
      .replace('{version}', version);
    return conf;
  }

  /**
   * Iterates through the given array creating script tags and then waiting for them
   * all to successfully load before resolving the returned promise.
   *
   * bootstrapConfigArray is of type { url: string, isEsModule: boolean }[]
   */
  async function loadScripts(bootstrapConfigArray) {
    return new Promise(function(resolve) {
      const promiseList = [];
      bootstrapConfigArray.forEach(bootstrapConfig => promiseList.push(scriptTagGenerator(bootstrapConfig)));
      Promise.all(promiseList).then((data) => {
        resolve();
      });
    });
  }

  /**
   * Gets the list of apps from LEConfig and then injects script tags to download the module's js from
   * the CDN.
   * @returns a Promise that resolves when all the scripts are loaded
   */
  async function loadApps() {
    /** bootstrapConfigArray: { url: string, isEsModule: boolean }[] */
    const bootstrapConfigArray = [];
    if (!LEConfig.leapps) {
      return Promise.resolve();
    }
    Object.keys(LEConfig.leapps).forEach(cdnName => {
      const conf = calculateConf(cdnName, LEConfig.leapps[cdnName].version, LEConfig.leapps[cdnName].useOrigLpCdnDomain);
      Object.assign(LEConfig.leapps[cdnName], {}, conf);

      // only set to true if explicitly set, otherwise false for backwards compatibility
      const isEsModule = LEConfig.leapps[cdnName].isEsModule === true;

      bootstrapConfigArray.push({url: `${conf.publicPath}${conf.filePath}`, isEsModule});
    });
    await loadScripts(bootstrapConfigArray);
    loadModules(LEConfig.leapps);
  }

  function getAppIdFromCdnName(cdnName) {
    const name = calculateAppNameFromCdnName(cdnName);
    return calculateApplicationAppIdFromName(name);
  }

  function extractAppFromWindow(cdnName){
    const appLibraryName = cdnName.replace(/-/g, '_');
    let appId = getAppIdFromCdnName(cdnName);
    if (appId === 'umv') {
      appId = 'um';
    }
    return es6MiddleUtils.removeEsModuleDefault(window[appId]) || es6MiddleUtils.removeEsModuleDefault(window[appLibraryName]);
  }

  function loadModules() {
    Object.keys(LEConfig.leapps).forEach(cdnName => {
      applications[cdnName] = extractAppFromWindow(cdnName);
      if(applications[cdnName]){
        if (typeof applications[cdnName].webpackPathInit === 'function') {
          applications[cdnName].webpackPathInit(LEConfig.leapps[cdnName]);
        }
        const appConfig = applications[cdnName].config;
        if (appConfig && !appConfig.isBackboneModule) {
          registerModule.registerExternal(appConfig);
        } else {
          logger.error(`Application ${cdnName} must contain config`);
        }
      } else{
        logger.error(`application ${cdnName} not loaded correctly`);
      }
    });
  }

  // TODO: after all uses their full name -> remove application project and get all lp_le from window.
  async function kickStartApps() {
    const locale = LocaleResolver.getLocale();
    const fallbackLocale = LocaleResolver.getDefaultLocale();
    if(Object.keys(applications).length > 0){
      await Promise.all(Object.keys(applications).map(async (storedAppId) => {
          try {
            const app = applications[storedAppId];
            const appConf = LEConfig.leapps[storedAppId];
            appConf.locale = locale;
            appConf.defaultLocale = fallbackLocale; // remove appConf.defaultLocale at 10.8
            appConf.fallbackLocale = fallbackLocale;
            if (app.applicationInit) {
              try {
                const {
                  appId,
                  routes,
                  i18nResource,
                  fallbackI18nResource,
                  appLocaleResource,
                  appFallbackLocaleResource,
                  vueComponent,
                  reactModule,
                } = await app.applicationInit(appConf, LEConfig);
                if (appId !== undefined) {
                  //if the external module will not provide routes, we won׳t add it to sidebar
                  if (routes) {
                    //routes configuration for sidebar routes
                    let updatedRoutes = routes;
                    if (reactModule) {
                      updatedRoutes = routes.map((route) => (
                        {
                          ...route,
                          // create a dummy Vue component as it is required by vue-router
                          component: { template: '' },
                          meta: {
                            ...route.meta,
                            // Add { react: true } for CCUI to identify this route as react
                            react: true
                          }
                        }
                      ));
                    }
                    store.dispatch(`${storeModuleTypes.ENVIRONMENT}/${environmentActionTypes.VALID_APPS_UPDATE}`, appId);
                    store.dispatch(`${storeModuleTypes.ENVIRONMENT}/${environmentActionTypes.ROUTES_UPDATE}`, updatedRoutes);
                    const route = routes.find(r => r.name === 'default') || undefined;
                    appConf.defaultRoute = route && route.redirect && route.redirect.path;
                  }
                  if (i18nResource || appLocaleResource) { // remove i18nResource at 10.8
                    i18n.i18next.addResourceBundle(locale, appId, i18nResource || appLocaleResource); // remove i18nResource at 10.8
                  }
                  if ((appFallbackLocaleResource || fallbackI18nResource) && locale !== fallbackLocale) { // remove fallbackI18nResource at 10.8
                    i18n.i18next.addResourceBundle(fallbackLocale, appId, appFallbackLocaleResource || fallbackI18nResource);// remove fallbackI18nResource at 10.8
                  }
                  if (reactModule){
                    window.reactModule = [...(window.reactModule || []), { appId, createRoot: reactModule.createRoot }];
                    window.reactElements = [...(window.reactElements || []), { appId, element: reactModule.element }];
                  }
                  if (vueComponent) {
                    mainVueApps.mountContainer.component(vueComponent.name, vueComponent);
                  }
                }
              }
              catch (e) {
                logger.error(`fail to load application ${storedAppId}: ${e}`);
              }
            }
          } catch (e) {
            logger.error(`fail to handle application config ${storedAppId}: ${e}`);
          }
        })
      );
      // remove lodash from the global scope.
      if (window._) {
        window._.noConflict();
      }
    }
  }

  /**
   * This function load a Backbone module dictionary and add it to the main dictionary
   * This dictionary is loaded from the backbone module app and isn't loaded from the languino project anymore.
   *
   * @param app - the Backbone app configuration
   *
   */
  async function loadBackboneModuleDictionary(app) {
    const locale = LocaleResolver.getLocale();
    const fallbackLocale = LocaleResolver.getDefaultLocale();
    if (app.config && app.config.isBackboneModule) {
      const moduleName = app.config.moduleName || '';
      if (app.getBackboneModuleDictionary) {
        try {
          const backboneModuleDictionary = await app.getBackboneModuleDictionary(locale, fallbackLocale);
          const dictionaryName = app.config.dictionaryName || app.config.moduleName;
          LE.translator.addModuleDictionary(locale, dictionaryName, backboneModuleDictionary.localResource);
          if (locale !== fallbackLocale) {
            LE.translator.addModuleDictionary(fallbackLocale, dictionaryName, backboneModuleDictionary.fallbackLocaleResource);
          }
        } catch (e) {
          logger.error(`fail to add backbone module dictionary ${moduleName} `, e);
        }
      } else {
        logger.error(`The Backbone module ${moduleName} must contains a getBackboneModuleDictionary function`);
      }
    }
  }

  function getApplications() {
    return applications;
  }

  function getApplicationConfigByCdnName(cndName) {
    return LEConfig.leapps[cndName];
  }

  return {
    kickStartApps,
    loadApps,
    getApplications,
    getApplicationConfigByCdnName,
    loadBackboneModuleDictionary,
  };
});
